import {
  ModelScope,
  useAction,
  useInScopeModel,
  useWatchableValue,
} from '../actions/framework';
import { ToolModel } from './tool.model';
import { GraphModel } from '../graph/graph.model';
import { Assert } from '@cotera/utilities';
import { useTabParams } from '../tab-manager/tab-manager';
import { NotFoundTab } from '../not-found.tab';
import { ToolConnections } from '../components/tool-connections/tool-connections-dialog';
import { useExistingConnections } from '@cotera/client/app/hooks/use-tools';
import { useState } from 'react';
import { Text, Icon, Tooltip } from '@cotera/client/app/components/ui';
import { SaveToolAction } from './save-tool.action';
import { SelectToolAction } from './select-tool.action';
import { Button } from '../components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@cotera/client/v0/components/ui/card';
import { Suspense } from 'react';
import { Loading } from '@cotera/client/app/components';
import { PROVIDER_ICONS } from '../components/tool-connections/tool-connections-dialog';
import { AppTab } from '../components/tab/tab';
import { DuplicateAction } from '../graph/duplicate.action';
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuShortcut,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { DeleteToolAction } from './delete-tool.action';
import { useToolSchemaLookup } from '@cotera/client/app/hooks/use-tool-schema';

type Tool = {
  id: string;
  name: string;
  displayName: string;
  toolConnectionId: string;
  enabled: boolean;
  createdAt: Date | string;
};

type Connection = {
  id: string;
  provider: string;
  displayName: string;
  tools: Tool[];
};

const useModel = () => {
  const { id } = useTabParams<{ id: string }>();
  const model = useInScopeModel(ToolModel, id);

  Assert.assert(model !== null, 'Tool Model not found');
  return model;
};

export const NewToolTab: React.FC = () => {
  const { id } = useTabParams<{ id: string }>();
  const graphModel = useInScopeModel(GraphModel);

  Assert.assert(graphModel !== null);

  const model = new ToolModel(
    {
      name: 'New Tool',
      graph: graphModel.graph,
      compiler: graphModel.compiler,
    },
    {
      id,
      isNew: true,
    }
  );

  return (
    <AppTab model={model}>
      <ModelScope models={[model]}>
        <ToolPage />
      </ModelScope>
    </AppTab>
  );
};

export const ToolTab: React.FC = () => {
  const { id: name } = useTabParams<{ tabId: string; id: string }>();
  const graphModel = useInScopeModel(GraphModel);

  Assert.assert(graphModel !== null);

  const graph = useWatchableValue(graphModel.graph);

  if (!graph.hasSymbol(name)) {
    return <NotFoundTab title="Tool Not Found" resource={`Tool "${name}"`} />;
  }

  const model = new ToolModel(
    {
      name,
      graph: graphModel.graph,
      compiler: graphModel.compiler,
    },
    {
      id: name,
    }
  );

  return (
    <AppTab model={model}>
      <ModelScope models={[model]}>
        <ToolPage />
      </ModelScope>
    </AppTab>
  );
};

const Header: React.FC = () => {
  const saveAction = useAction(SaveToolAction);
  const duplicateTool = useAction(DuplicateAction);
  const deleteTool = useAction(DeleteToolAction);

  return (
    <div className="absolute top-2 right-2">
      <Tooltip tooltipContent="Save" side="auto">
        <Button
          variant={'outline'}
          className="mb-2 ml-2"
          onClick={() => saveAction.execute()}
        >
          <Icon icon="save" />
          Save
        </Button>
      </Tooltip>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size={'icon-sm'}>
            <Icon icon="ellipsis-vertical" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" side="bottom">
          <DropdownMenuItem onSelect={() => duplicateTool.execute()}>
            <Icon icon="copy" />
            Duplicate Tool
            <DropdownMenuShortcut shortcut={duplicateTool.action?.shortcut} />
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => deleteTool.execute()}>
            <Icon icon="trash" />
            Delete Tool
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

const ToolPage: React.FC = () => {
  const model = useModel();
  Assert.assert(model !== null, 'Model not found');

  const name = useWatchableValue(model.name);
  const errors = useWatchableValue(model.errors);
  const isNew = model.isNew;
  const selectToolAction = useAction(SelectToolAction);
  const toolMetadata = useWatchableValue(model.toolMetadata);

  // For new tools, show the tool selection interface
  if (isNew && toolMetadata === null) {
    return (
      <div className="flex flex-col h-full p-6">
        <div className="mb-6">
          <Text.Title>Install Tools</Text.Title>
          <Text.Caption className="text-muted-foreground mt-2">
            Search for new tools to install into your Cotera instance.
          </Text.Caption>
        </div>

        <div className="flex-1 min-h-0">
          <Suspense
            fallback={
              <div className="flex flex-col gap-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Loading.Shimmer key={i} className="h-24 rounded-lg" />
                ))}
              </div>
            }
          >
            <ToolConnections
              onSelect={async (tool) => {
                await selectToolAction.execute({
                  toolId: tool.id,
                  toolConnectionId: tool.toolConnectionId,
                  name: tool.name,
                  displayName: tool.displayName,
                  isExisting: tool.isExisting,
                });
              }}
              className="h-full"
              toolsClassName="max-h-full"
            />
          </Suspense>
        </div>
      </div>
    );
  }

  // For new tools after selection, show tool info and credentials
  if (isNew && toolMetadata !== null) {
    return (
      <div className="flex flex-col h-full">
        <Header />
        <div className="flex flex-col h-full p-6 gap-6">
          <div>
            <Text.Title>Tool Selected</Text.Title>
            <Text.Caption className="text-muted-foreground mt-2">
              {name}
            </Text.Caption>
          </div>

          {errors.length > 0 && (
            <div className="mt-2 p-2 bg-red-50 text-red-800 rounded">
              {errors.map((error, i) => (
                <div key={i}>{JSON.stringify(error)}</div>
              ))}
            </div>
          )}

          <div className="mt-4 text-muted-foreground">
            Click save to add this tool to your workspace.
          </div>

          <Suspense fallback={<Loading.Dots />}>
            <ToolSections />
          </Suspense>
        </div>
      </div>
    );
  }

  // For existing tools, show credentials management
  return (
    <div className="flex flex-col h-full">
      <Header />
      <div className="flex flex-col h-full p-6 gap-6">
        <div>
          <Text.Title subtitle={toolMetadata?.toolConnectionId}>
            {name}
          </Text.Title>
        </div>

        {errors.length > 0 && (
          <div className="mt-2 p-2 bg-red-50 text-red-800 rounded">
            {errors.map((error, i) => (
              <div key={i}>{JSON.stringify(error)}</div>
            ))}
          </div>
        )}

        <Suspense fallback={<Loading.Dots />}>
          <ToolSections />
        </Suspense>
      </div>
    </div>
  );
};

// Wrapper component that fetches connections once and passes to both sections
const ToolSections: React.FC = () => {
  const { data: connections } = useExistingConnections();
  const model = useModel();
  const toolMetadata = useWatchableValue(model.toolMetadata);

  // Find the tool in the connections by ID (shared logic)
  const currentTool = toolMetadata?.toolId
    ? connections
        .flatMap((conn) => conn.tools)
        .find((tool) => tool.id === toolMetadata.toolId)
    : null;

  if (!currentTool) {
    return null;
  }

  const currentConnection = connections.find(
    (conn) => conn.id === currentTool.toolConnectionId
  );

  return (
    <>
      <CredentialsSection
        currentTool={currentTool}
        currentConnection={currentConnection}
        connections={connections}
      />
      <TypeInfoSection currentTool={currentTool} />
    </>
  );
};

const TypeInfoSection: React.FC<{ currentTool: Tool }> = ({ currentTool }) => {
  const getToolSchema = useToolSchemaLookup();
  const toolSchema = getToolSchema(currentTool.name);

  if (toolSchema === null) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Input & Output Types</CardTitle>
        <CardDescription>Type information for this tool</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-sm font-medium mb-2">Input Parameters</div>
            <pre className="text-xs bg-muted p-3 rounded-md overflow-auto max-h-96">
              <code>{toolSchema.inputSchema.prettyPrint()}</code>
            </pre>
          </div>

          <div>
            <div className="text-sm font-medium mb-2">Output Type</div>
            <pre className="text-xs bg-muted p-3 rounded-md overflow-auto max-h-96">
              <code>{toolSchema.outputSchema.prettyPrint()}</code>
            </pre>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const CredentialsSection: React.FC<{
  currentTool: Tool;
  currentConnection: Connection | undefined;
  connections: Connection[];
}> = ({ currentTool, currentConnection }) => {
  const [showForm, setShowForm] = useState(false);

  const otherToolsUsingConnection =
    currentConnection?.tools.filter((t) => t.id !== currentTool.id) ?? [];

  const provider = currentConnection?.provider;
  const ProviderIcon = provider
    ? PROVIDER_ICONS[provider as keyof typeof PROVIDER_ICONS] ?? (() => null)
    : () => null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Connection</CardTitle>
        <CardDescription>
          Manage the connection used by this tool
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-col gap-2">
          <div className="text-sm font-medium">Current Connection</div>
          <div className="text-sm text-muted-foreground">
            {(() => {
              const defaultName = `${
                currentConnection?.provider || 'Unknown'
              } Connection`;
              const customName = currentConnection?.displayName;

              // Only show custom name if it differs from default
              if (customName && customName !== defaultName) {
                return customName;
              }
              return <span className="capitalize">{defaultName}</span>;
            })()}
          </div>
          {(currentConnection?.tools.length ?? 0) > 1 && (
            <div className="text-xs text-muted-foreground">
              {currentConnection?.tools.length ?? 0} tool
              {(currentConnection?.tools.length ?? 0) !== 1 ? 's' : ''} using
              this connection
            </div>
          )}

          {otherToolsUsingConnection.length > 0 && (
            <div className="mt-2 p-2 bg-muted/50 rounded-md">
              <div className="text-xs font-medium mb-1">
                Other tools using this connection:
              </div>
              <div className="text-xs text-muted-foreground">
                {otherToolsUsingConnection.map((t) => t.displayName).join(', ')}
              </div>
            </div>
          )}
        </div>

        {!showForm && (
          <div className="flex flex-row justify-between border-t border-border pt-4 items-center">
            <Text.Caption className="text-muted-foreground">
              {otherToolsUsingConnection.length > 0
                ? 'You can choose to update connection for just this tool or all tools using this connection.'
                : 'Updating connection will switch this tool to use a different connection.'}
            </Text.Caption>
            <Button
              onClick={() => setShowForm(true)}
              variant="outline"
              className="w-fit"
            >
              <Icon icon="key" className="mr-2" />
              Update Connection
            </Button>
          </div>
        )}

        {showForm && provider && (
          <>
            <div className="border-t border-border my-4" />

            <div className="flex items-center gap-2 mb-4">
              <ProviderIcon />
              <div>
                <div className="text-sm font-medium">Update Connection</div>
                <div className="text-xs text-muted-foreground capitalize">
                  Configure new {provider} connection via the tool connections
                  dialog.
                </div>
              </div>
            </div>

            <div className="flex gap-2 justify-end w-full mt-4">
              <Button
                onClick={() => setShowForm(false)}
                variant="outline"
                type="button"
              >
                Cancel
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
